import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Spinner,
  Card,
} from "react-bootstrap";
import { GetUserInfo } from "../../../utils/permissionChecker";
import useUserApi from "../../../services/userApi";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import AlertModal from "../../../components/UiComponent/AlertModal";

const ChangeUserPassword = () => {
  const { ChangeUsrPassword } = useUserApi();
  const { t } = useTranslation();
  const user = GetUserInfo();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [verifyPassword, setVerifyPassword] = useState("");
  const [formError, setFormError] = useState("");

  // Alert Modal
  const [alertTitle, setAlertTitle] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const [showAlert, setShowAlert] = useState(false);

  const handleCloseAlert = () => setShowAlert(false);

  const changePasswordMutation = useMutation({
    mutationFn: (data) => ChangeUsrPassword(data),
    onSuccess: () => {
      toast.success("Password changed successfully!", {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });

      // Reset form
      setCurrentPassword("");
      setNewPassword("");
      setVerifyPassword("");
      setFormError("");
    },
    onError: (error) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to change password. Please try again.";

      setAlertTitle("Error");
      setAlertMessage(message);
      setShowAlert(true);
    },
    onSettled: () => {
      setIsSubmitting(false);
    },
  });

  const handleFormSubmit = (e) => {
    e.preventDefault();
    setFormError("");

    if (!currentPassword || !newPassword || !verifyPassword) {
      setFormError("All fields are required.");
      return;
    }

    if (newPassword !== verifyPassword) {
      setFormError("New password and confirmation do not match.");
      return;
    }

    if (newPassword.length < 6) {
      setFormError("New password must be at least 6 characters long.");
      return;
    }

    const payload = {
      username: user.username,
      oldPassword: currentPassword,
      newPassword: newPassword,
    };

    setIsSubmitting(true);
    changePasswordMutation.mutate(payload);
  };

  const handleReset = () => {
    setCurrentPassword("");
    setNewPassword("");
    setVerifyPassword("");
    setFormError("");
  };

  return (
    <>
      <Container fluid className="py-4">
        <Row className="justify-content-center">
          <Col lg="6" md="8" sm="12">
            <Card>
              <Card.Header className="bg-white border-0">
                <h4 className="card-title mb-0">Change Password</h4>
              </Card.Header>
              <Card.Body>
                <Form onSubmit={handleFormSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>Current Password</Form.Label>
                    <Form.Control
                      type="password"
                      placeholder="Enter your current password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      disabled={isSubmitting}
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>New Password</Form.Label>
                    <Form.Control
                      type="password"
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      disabled={isSubmitting}
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Confirm New Password</Form.Label>
                    <Form.Control
                      type="password"
                      placeholder="Re-type new password"
                      value={verifyPassword}
                      onChange={(e) => setVerifyPassword(e.target.value)}
                      disabled={isSubmitting}
                      required
                    />
                  </Form.Group>

                  {formError && (
                    <div className="alert alert-danger small py-2" role="alert">
                      {formError}
                    </div>
                  )}

                  <div className="d-flex mt-4">
                    <Button
                      type="button"
                      variant="outline-secondary"
                      onClick={handleReset}
                      disabled={isSubmitting}
                      className="mr-3"
                    >
                      {t("cancel") || "Cancel"}
                    </Button>

                    <Button
                      type="submit"
                      variant="primary"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-2"
                          />
                          Processing...
                        </>
                      ) : (
                        "Change Password"
                      )}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Error Alert Modal */}
      <AlertModal
        show={showAlert}
        onHide={handleCloseAlert}
        title={alertTitle}
        message={alertMessage}
      />
    </>
  );
};

export default ChangeUserPassword;
